import {
  AccountTree,
  Bolt,
  Business,
  CardMembership,
  Category,
  Collections,
  CorporateFare,
  Event,
  Flag,
  Group,
  Key,
  LocalOffer,
  Mail,
  ManageAccounts,
  Mood,
  Newspaper,
  OndemandVideo,
  Person,
  PhotoLibrary,
  Psychology,
  RocketLaunch,
  Public,
  Sell,
  Settings,
  SpaceDashboard,
  Storefront,
  Summarize,
  Thermostat,
  Upload,
  VideoLibrary,
  Widgets,
} from '@mui/icons-material'

import NavigationIconCompareGames from '../assets/images/navigation-icon-compare-games.svg'
import NavigationIconDailyInsights from '../assets/images/navigation-icon-daily-insights.svg'
import NavigationIconFollowedGames from '../assets/images/navigation-icon-followed-games.svg'
import NavigationIconFTUEVideos from '../assets/images/navigation-icon-ftue-videos.svg'
import NavigationIconGameAnalyzer from '../assets/images/navigation-icon-game-analyzer.svg'
import NavigationIconGameUpdateImpacts from '../assets/images/navigation-icon-game-update-impacts.svg'
import NavigationIconGenreEssentials from '../assets/images/navigation-icon-genre-essentials.svg'
import NavigationIconImplementationExamples from '../assets/images/navigation-icon-implementation-examples.svg'
import NavigationIconLiveEventsTracker from '../assets/images/navigation-icon-live-events-tracker.svg'
import NavigationIconMarketExplorer from '../assets/images/navigation-icon-market-explorer.svg'
import NavigationIconMarketShare from '../assets/images/navigation-icon-market-share.svg'
import NavigationIconMarketTrends from '../assets/images/navigation-icon-market-trends.svg'
import NavigationIconReports from '../assets/images/navigation-icon-reports.svg'
import NavigationIconSoftLaunch from '../assets/images/navigation-icon-soft-launch.svg'
import NavigationIconTopGrossing from '../assets/images/navigation-icon-top-grossing.svg'
import NavigationIconVisualExplorer from '../assets/images/navigation-icon-visual-explorer.svg'
import SlackIcon from '../assets/images/slack-icon.svg'
import { internalAdminRoles, RoleEnum } from '../features/account'
import { Page } from '../types/Page'
import { SideNavigationGroupData } from '../types/SideNavigationGroupData'
import NavigationIconAIAnalyst from './../assets/images/navigation-icon-ai-analyst.svg'

const internalDashboardPageId = 'internal-dashboard'

// The legacy internal app's Analysts order, with Live Events Tracking, which legacy doesn't have, right after the Dashboard.
// Pages not listed keep their definition order after these.
const internalSidebarOrder = [
  internalDashboardPageId,
  'internal-tracked-games',
  'internal-analysis-suggestions',
  'internal-brands',
  'internal-categories',
  'internal-ftue-videos',
  'internal-games',
  'internal-reports',
  'internal-analysts',
  'internal-implementation-examples',
  'internal-implementation-bulk-assign',
  'internal-import-tool',
]

const withRequiredRoles = (requiredRoles: RoleEnum[], pages: Page[]) => pages.map((page) => Object.assign(page, { requiredRoles }))

class PageService {
  pages: Array<Page>
  internalPages: Array<Page>
  internalAdminPages: Array<Page>

  constructor() {
    this.pages = [
      new Page('data-glossary-detail', '/data-glossary/*', 'sidebar:data_glossary', 'sidebar', ''),
      new Page('data-glossary', '/data-glossary', 'sidebar:data_glossary', 'sidebar', ''),
      new Page('daily-insights', '/daily-insights', 'sidebar:daily_insights', 'sidebar', 'daily-data', NavigationIconDailyInsights),
      new Page('daily-insights-wildcard', '/daily-insights/*', 'sidebar:daily_insights', '', ''),
      new Page(
        'genre-essentials',
        '/genre-essentials/*',
        'sidebar:genre_essentials',
        'sidebar',
        'daily-data',
        NavigationIconGenreEssentials,
        undefined,
        'genre-essentials'
      ),
      new Page('top-grossing', '/top-grossing', 'sidebar:topgrossing', 'sidebar', 'daily-data', NavigationIconTopGrossing),
      new Page('soft-launch', '/soft-launch', 'sidebar:soft_launch', 'sidebar', 'daily-data', NavigationIconSoftLaunch),

      new Page('market-explorer', '/market-explorer/:subpage/*', 'sidebar:market_explorer', '', ''),
      new Page('market-explorer', '/market-explorer', 'sidebar:market_explorer', 'sidebar', 'market-research', NavigationIconMarketExplorer),
      new Page('market-share', '/market-share/:subpage', 'sidebar:market_share', '', ''),
      new Page('market-share', '/market-share', 'sidebar:market_share', 'sidebar', 'market-research', NavigationIconMarketShare),
      new Page(
        'visual-explorer',
        '/visual-explorer/*',
        'sidebar:visuals_explorer',
        'sidebar',
        'market-research',
        NavigationIconVisualExplorer,
        undefined,
        '/visual-explorer'
      ),
      new Page('report-detail', '/reports/:reportId/*', 'sidebar:reports', '', ''),
      new Page('reports', '/reports/*', 'sidebar:reports', 'sidebar', 'market-research', NavigationIconReports, undefined, '/reports'),

      new Page('market-trends', '/market-trends', 'sidebar:market-trends', 'sidebar', 'market-research', NavigationIconMarketTrends),
      new Page('market-trends', '/market-trends/*', 'sidebar:market-trends', '', ''),
      new Page('live-events-tracker', '/live-events-tracker/:subpage', 'sidebar:live_events_tracker', ''),
      new Page(
        'live-events-tracker',
        '/live-events-tracker',
        'sidebar:live_events_tracker',
        'sidebar',
        'market-research',
        NavigationIconLiveEventsTracker,
        undefined,
        '/live-events-tracker/feed'
      ),
      new Page(
        'ai-analyst',
        '/ai-analyst',
        'sidebar:ai_analyst',
        'sidebar',
        'market-research',
        NavigationIconAIAnalyst,
        undefined,
        undefined,
        RoleEnum.ai_analyst,
        true
      ),

      new Page('followed-games', '/followed-games', 'sidebar:followed_games', 'sidebar', 'game-analysis', NavigationIconFollowedGames),
      new Page('compare-games', '/compare-games/:subpage/*', 'sidebar:game_comparison', '', ''),
      new Page('compare-games', '/compare-games', 'sidebar:game_comparison', 'sidebar', 'game-analysis', NavigationIconCompareGames),
      new Page(
        'game-update-impacts',
        '/game-update-impacts/*',
        'sidebar:game-update-impacts',
        'sidebar',
        'game-analysis',
        NavigationIconGameUpdateImpacts,
        undefined,
        '/game-update-impacts'
      ),

      new Page('implementation-examples', '/implementation-examples/:tab/:subtab?/*', 'sidebar:impl_examples', '', ''),
      new Page(
        'implementation-examples',
        '/implementation-examples',
        'sidebar:impl_examples',
        'sidebar',
        'game-analysis',
        NavigationIconImplementationExamples
      ),

      new Page('ftue-videos', '/ftue-videos', 'sidebar:ftue-videos', 'sidebar', 'game-analysis', NavigationIconFTUEVideos),
      new Page('ftue-videos', '/ftue-videos/*', 'sidebar:ftue-videos', '', ''),

      new Page('game-analyzer-detail', '/game-analyzer/:id/*', 'sidebar:game_analyzer', '', ''),
      new Page(
        'game-analyzer',
        '/game-analyzer/*',
        'sidebar:game_analyzer',
        'sidebar',
        'game-analysis',
        NavigationIconGameAnalyzer,
        undefined,
        '/game-analyzer'
      ),

      new Page('news-card', '/news-card/:id', 'newsfeed:newscard', '', ''),

      new Page('game-tab', '/game/:id/:subpage/:subtab?/*', 'common:game', '', ''),

      new Page('game', '/game/:id', 'common:game', '', ''),

      new Page('my-profile', '/my-profile', 'profile:my_profile', 'account', '', '', Person),
      new Page('organization', '/organization', 'organization:organization', 'account', '', '', Business),
      new Page('organization-users', '/users', 'organization:users', 'account', '', '', Group),
      new Page('slack', '/slack', 'newsfeed:slack', 'account', '', SlackIcon),
      new Page('slack-preferences', '/slack/preferences/:teamName/:teamId/:channel/*', 'newsfeed:slack_preferences', '', ''),
      new Page('invites', '/invites', 'profile:invites', 'account', '', '', Mail),
    ]

    this.internalPages = [
      new Page(
        'internal-tracked-games',
        '/internal/live-events-tracking',
        'internal-sidebar:live_events_tracking',
        'analysts',
        'analysts',
        '',
        Event,
        undefined,
        [RoleEnum.internal, RoleEnum.let_internal],
        undefined,
        'internal-sidebar:live_events_tracking_tracked_games'
      ),
      new Page('internal-tracked-game-events', '/internal/live-events-tracking/events/:trackedGameId', 'internal-sidebar:live_events_tracking_events', ''),
      new Page(
        'internal-tracked-game-events-edit-event',
        '/internal/live-events-tracking/events/:trackedGameId/event/:eventId?/edit',
        'internal-sidebar:live_events_tracking_events',
        ''
      ),
      new Page('internal-tracked-games-search', '/internal/live-events-tracking/gamesearch', 'internal-sidebar:live_events_tracking_track_new_game', ''),
      new Page(
        'internal-tracked-game-event-analysis',
        '/internal/live-events-tracking/events/:trackedGameId/event/:eventId?/analysis',
        'internal-sidebar:live_events_tracking_events',
        ''
      ),
      new Page(
        'internal-tracked-game-analyst-note-edit',
        '/internal/live-events-tracking/events/:trackedGameId/analyst-notes/edit/analyst-notes/',
        'internal-sidebar:live_events_analyst_notes',
        ''
      ),
      new Page(
        'internal-tracked-game-analyst-notes',
        '/internal/live-events-tracking/events/:trackedGameId/analyst-notes',
        'internal-sidebar:live_events_analyst_notes',
        ''
      ),
      new Page(
        'internal-tracked-game-events-overview',
        '/internal/live-events-tracking/events/:trackedGameId/overview',
        'internal-sidebar:live_events_overview',
        ''
      ),
      new Page(
        'internal-event-archetypes-edit',
        '/internal/live-events-tracking/events/:trackedGameId/archetypes/edit',
        'internal-sidebar:live_events_archetypes_edit',
        ''
      ),
      new Page(
        'internal-event-archetype-edit',
        '/internal/live-events-tracking/events/:trackedGameId/archetypes/edit/:archetypeId',
        'internal-sidebar:live_events_archetypes_edit',
        ''
      ),
      new Page(
        'internal-market-level-blueprints-edit',
        '/internal/live-events-tracking/market-level-blueprints/edit/:archetypeId?',
        'internal-sidebar:live_events_market_level_blueprints_edit',
        ''
      ),
      new Page(
        'internal-market-level-blueprints-overview',
        '/internal/live-events-tracking/market-level-blueprints/overview/:archetypeId?',
        'internal-sidebar:live_events_market_level_blueprints_overview',
        ''
      ),
      ...withRequiredRoles(
        [RoleEnum.internal],
        [
          new Page(
            'internal-ai-prompts',
            '/internal/ai-prompts',
            'internal-sidebar:ai_prompts',
            'ai-config',
            'ai-config',
            '',
            Psychology,
            undefined,
            RoleEnum.internal
          ),
          new Page('internal-ai-prompt-detail', '/internal/ai-prompts/:type/:name', 'internal-sidebar:ai_prompt_detail', ''),
        ]
      ),
    ]

    this.internalAdminPages = withRequiredRoles(internalAdminRoles, [
      new Page(
        internalDashboardPageId,
        '/internal/dashboard',
        'internal-sidebar:dashboard',
        'analysts',
        'analysts',
        '',
        SpaceDashboard,
        undefined,
        RoleEnum.admin
      ),
      new Page('internal-brands', '/internal/brands', 'internal-sidebar:brands', 'analysts', 'analysts', '', Flag, undefined, RoleEnum.admin),
      new Page(
        'internal-brand-edit',
        '/internal/brands/edit/:brandId?',
        'internal-sidebar:brand_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page('internal-games', '/internal/games', 'internal-sidebar:games', 'analysts', 'analysts', '', RocketLaunch, undefined, RoleEnum.admin),
      new Page('internal-categories', '/internal/categories', 'internal-sidebar:categories', 'analysts', 'analysts', '', Widgets, undefined, RoleEnum.admin),
      new Page(
        'internal-game-review-edit',
        '/internal/games/:appId/review/:reviewId?',
        'internal-sidebar:game_review_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-analysis-comment-edit',
        '/internal/games/:gameId/comments/:commentId?',
        'internal-sidebar:analysis_comment_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-category-feature-edit',
        '/internal/categories/:categoryId/features/:featureId?',
        'internal-sidebar:category_feature_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-analysis-overview',
        '/internal/analysis/:analysisId/overview',
        'internal-sidebar:analysis_overview',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-analysis-feature-choices',
        '/internal/analysis/:analysisId/categories/:categoryId',
        'internal-sidebar:analysis_feature_choices',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-feature-bulk-assign',
        '/internal/features/:featureLegacyId/bulk-assign',
        'internal-sidebar:feature_bulk_assign',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-implementation-examples',
        '/internal/implementation-examples',
        'internal-sidebar:implementation_examples',
        'analysts',
        'analysts',
        '',
        PhotoLibrary,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-implementation-screenshots',
        '/internal/implementation-examples/:gameId/screenshots',
        'internal-sidebar:implementation_screenshots',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-screenshot-comment-edit',
        '/internal/implementation-examples/:gameId/comments',
        'internal-sidebar:screenshot_comment_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-implementation-bulk-assign',
        '/internal/implementation-bulk-assign',
        'internal-sidebar:ie_bulk_assign',
        'analysts',
        'analysts',
        '',
        Collections,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-implementation-bulk-assign-edit',
        '/internal/implementation-bulk-assign/edit',
        'internal-sidebar:ie_bulk_assign_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-implementation-bulk-assign-mode',
        '/internal/implementation-bulk-assign/:mode',
        'internal-sidebar:ie_bulk_assign',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-analysis-screenshots',
        '/internal/analysis/:analysisId/screenshots',
        'internal-sidebar:analysis_screenshots',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-analysis-screenshots-upload',
        '/internal/analysis/:analysisId/screenshots/upload',
        'internal-sidebar:analysis_screenshots_upload',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-analysis-screenshots-edit',
        '/internal/analysis/:analysisId/screenshots/edit',
        'internal-sidebar:analysis_screenshots_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page('internal-analysts', '/internal/analysts', 'internal-sidebar:analysts', 'analysts', 'analysts', '', Person, undefined, RoleEnum.admin),
      new Page('internal-import-tool', '/internal/import-tool', 'internal-sidebar:import_tool', 'analysts', 'analysts', '', Upload, undefined, RoleEnum.admin),
      new Page('internal-reports', '/internal/reports', 'internal-sidebar:reports', 'analysts', 'analysts', '', Summarize, undefined, RoleEnum.admin),
      new Page(
        'internal-report-edit',
        '/internal/reports/edit/:reportId?',
        'internal-sidebar:report_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-ftue-videos',
        '/internal/ftue-videos',
        'internal-sidebar:ftue_videos',
        'analysts',
        'analysts',
        '',
        VideoLibrary,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-ftue-video-attach',
        '/internal/ftue-videos/attach',
        'internal-sidebar:ftue_video_attach',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-ftue-video-edit',
        '/internal/ftue-videos/:videoId/edit',
        'internal-sidebar:ftue_video_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-analysis-suggestions',
        '/internal/analysis-suggestions',
        'internal-sidebar:analysis_suggestions',
        'analysts',
        'analysts',
        '',
        Thermostat,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-editorial-cards',
        '/internal/editorial-cards',
        'internal-sidebar:editorial_cards',
        'marketing',
        'marketing',
        '',
        Newspaper,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-editorial-card-edit',
        '/internal/editorial-cards/edit/:cardId?',
        'internal-sidebar:editorial_card_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-ad-data-tester',
        '/internal/ad-data-tester',
        'internal-sidebar:ad_data_tester',
        'service-management',
        'service-management',
        '',
        Bolt,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-conventional-genres',
        '/internal/conventional-genres',
        'internal-sidebar:conventional_genres',
        'service-management',
        'service-management',
        '',
        AccountTree,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-keywords',
        '/internal/keywords',
        'internal-sidebar:keywords',
        'service-management',
        'service-management',
        '',
        Key,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-markets',
        '/internal/markets',
        'internal-sidebar:markets',
        'service-management',
        'service-management',
        '',
        Public,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-metadata-tags',
        '/internal/metadata-tags',
        'internal-sidebar:metadata_tags',
        'service-management',
        'service-management',
        '',
        LocalOffer,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-motivations',
        '/internal/motivations/:tab?',
        'internal-sidebar:motivations',
        'service-management',
        'service-management',
        '',
        Mood,
        '/internal/motivations/overrides',
        RoleEnum.admin
      ),
      new Page(
        'internal-onboarding-videos',
        '/internal/onboarding-videos',
        'internal-sidebar:onboarding_videos',
        'csm',
        'csm',
        '',
        OndemandVideo,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-onboarding-video-add',
        '/internal/onboarding-videos/add',
        'internal-sidebar:onboarding_video_add',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-onboarding-video-edit',
        '/internal/onboarding-videos/:videoId/edit',
        'internal-sidebar:onboarding_video_edit',
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-organizations',
        '/internal/organizations',
        'internal-sidebar:organizations',
        'csm',
        'csm',
        '',
        CorporateFare,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-organization-access',
        '/internal/organizations/:organizationId/access/:tab?',
        'internal-sidebar:organization_access',
        '',
        undefined,
        '',
        undefined,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-plans',
        '/internal/plans/:tab?',
        'internal-sidebar:plans',
        'csm',
        'csm',
        '',
        CardMembership,
        '/internal/plans/packages',
        RoleEnum.admin
      ),
      new Page(
        'internal-settings',
        '/internal/settings/:tab?',
        'internal-sidebar:settings',
        'service-management',
        'service-management',
        '',
        Settings,
        '/internal/settings/system-actions',
        RoleEnum.admin
      ),
      new Page(
        'internal-stores',
        '/internal/stores',
        'internal-sidebar:stores',
        'service-management',
        'service-management',
        '',
        Storefront,
        undefined,
        RoleEnum.admin
      ),
      new Page(
        'internal-tag-groups',
        '/internal/tag-groups',
        'internal-sidebar:tag_groups',
        'service-management',
        'service-management',
        '',
        Category,
        undefined,
        RoleEnum.admin
      ),
      new Page('internal-tags', '/internal/tags', 'internal-sidebar:tags', 'service-management', 'service-management', '', Sell, undefined, RoleEnum.admin),
      new Page('internal-tags-assign', '/internal/tags/assign', 'internal-sidebar:tags_assign', '', undefined, '', undefined, undefined, RoleEnum.admin),
      new Page('internal-users', '/internal/users', 'internal-sidebar:users', 'csm', 'csm', '', ManageAccounts, undefined, RoleEnum.admin),
    ])
  }

  getSideNavigationGroups() {
    return [
      new SideNavigationGroupData(
        'daily-data',
        'sidebar:daily_data',
        this.pages.filter((page) => page.groupId === 'daily-data')
      ),
      new SideNavigationGroupData(
        'market-research',
        'sidebar:market_research',
        this.pages.filter((page) => page.groupId === 'market-research')
      ),
      new SideNavigationGroupData(
        'game-analysis',
        'sidebar:game_analysis',
        this.pages.filter((page) => page.groupId === 'game-analysis')
      ),
    ]
  }

  getInternalSideNavigationGroups() {
    const sidebarPosition = (page: Page) => {
      const position = internalSidebarOrder.indexOf(page.id)
      return position === -1 ? internalSidebarOrder.length : position
    }
    const pages = [...this.internalPages, ...this.internalAdminPages].sort((a, b) => sidebarPosition(a) - sidebarPosition(b))
    const pagesInGroup = (groupId: string) => pages.filter((page) => page.groupId === groupId)

    return [
      new SideNavigationGroupData('analysts', 'internal-sidebar:group_analysts', pagesInGroup('analysts')),
      new SideNavigationGroupData('csm', 'internal-sidebar:group_csm', pagesInGroup('csm')),
      new SideNavigationGroupData('marketing', 'internal-sidebar:group_marketing', pagesInGroup('marketing')),
      new SideNavigationGroupData('service-management', 'internal-sidebar:group_service_management', pagesInGroup('service-management')),
      new SideNavigationGroupData('ai-config', 'internal-sidebar:group_ai_config', pagesInGroup('ai-config')),
    ].filter((group) => group.pages.length > 0)
  }

  getPageWithId(pageId: string) {
    return this.pages.find((page) => page.id === pageId)
  }

  getInternalPageWithId(pageId: string) {
    return [...this.internalPages, ...this.internalAdminPages].find((page) => page.id === pageId)
  }
}

const pageService = new PageService()

export default pageService
